// Third-party
import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import classNames from 'classnames';
import { Waypoint } from 'react-waypoint';
import { SortingRule } from 'react-table';

// Components
import { LoadingSpinner } from '../../common';
import { SearchInput } from '../../inputs';
import { GenericEmptyState } from '../../misc';
import { PortfoliosTable } from '../../tables';
import DisclaimerFooter from '../../disclaimers/DisclaimerFooter';
import { ControlItem, Tabs } from '../../navigation';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../../enums/Portfolio';

// Utils
import { formatMessage } from '../../../../utils/i18n';
import { getPortfolioMissingHoldings } from '../../../../utils/portfolio/utils';
import { PERMISSIONS, userHasPermission } from '../../../../utils/permissions';
import Portfolio from '@/types/portfolio';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import User from '@/types/user';

enum PortfolioOption {
  ALL_MODELS,
  MY_MODELS,
  ENTERPRISE_MODELS,
}

type Props = {
  query?: string;
  portfolios?: Portfolio[];
  myPortfolios?: Portfolio[];
  enterprisePortfolios?: Portfolio[];
  enterprise?: EnterpriseDetails;
  selectedPortfolios?: Portfolio[];
  loading: boolean;
  hasMorePortfolios: boolean;
  hasMoreMyPortfolios: boolean;
  hasMoreEnterprisePortfolios: boolean;
  onSort: (sorting: SortingRule[]) => void;
  loadMorePortfolios: () => void;
  loadMoreMyPortfolios: () => void;
  loadMoreEnterprisePortfolios: () => void;
  resetSelectedPortfolios: () => void;
  onComparePortfolio?: (portfolios: Portfolio[]) => void;
  onSearchPortfolios: (query: string) => void;
  onEditPortfolio?: (portfolio: Portfolio) => void;
  onDeletePortfolio?: (portfolios: Portfolio[]) => void;
  onSelectPortfolio?: (portfolio: Portfolio) => void;
  onPortfoliosSelected?: (portfolios: Portfolio[]) => void;
  onUnselectPortfolios?: (portfolios: Portfolio[]) => void;
  emptyStateAction?: () => void;
  emptyStateHeader: string;
  emptyStateBody?: string;
  emptyStateActionLabel?: string;
  displayEnterpriseLabels?: boolean;
  scrollAncestor?: Window | HTMLElement | string;
  hideStats: boolean;
  comparingPortfolioIds?: string[];
  displayedColumns: PORTFOLIO_TABLE_COLUMNS[];
  displayedMetrics: PORTFOLIO_METRICS_FIELDS[];
  selectedTitle: string;
  user: User;
  isThemeLight?: boolean;
};

type State = {
  initialized: boolean;
  selectedOption: PortfolioOption;
};

class LegacyModelsPortfoliosList extends React.Component<Props, State> {
  private scrollContainer?: HTMLElement;

  static defaultProps = {
    includeSearch: true,
    onSearchPortfolios: () => {},
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      initialized: false,
      selectedOption: this.getOptions().length > 0 ? this.getOptions()[0].value : PortfolioOption.ALL_MODELS,
    };
  }

  setContainerRef = (element: HTMLElement) => {
    this.scrollContainer = element;

    if (!this.state.initialized) {
      this.setState({ initialized: true });
    }
  };

  renderEmptyState = () => {
    const { emptyStateBody, emptyStateHeader, emptyStateActionLabel } = this.props;

    return (
      <GenericEmptyState
        header={emptyStateHeader}
        body={emptyStateBody}
        actionIcon="icon-arrow-up-right2"
        actionLabel={emptyStateActionLabel}
        onAction={this.props.emptyStateAction}
      />
    );
  };

  renderNoResultsState = () => (
    <GenericEmptyState
      header={formatMessage('EmptyState.NoResults.Title')}
      body={formatMessage('EmptyState.NoResults.Text')}
      actionLabel={formatMessage('EmptyState.NoResults.ClearSearch')}
      onAction={this.clearSearch}
    />
  );

  clearSearch = () => {
    this.props.onSearchPortfolios('');
  };

  isPortfolioSelected = (portfolio: Portfolio) => {
    const { selectedPortfolios } = this.props;

    if (!selectedPortfolios) {
      return false;
    }

    const isSelected = selectedPortfolios.find((p: Portfolio) => p._id === portfolio._id);

    return !!isSelected;
  };

  showLockColumn = () => {
    if (this.props.enterprise) {
    }
  };

  renderNoResults = () => {
    if (this.props.query) {
      return (
        <GenericEmptyState
          header={formatMessage('EmptyState.NoResults.Title')}
          body={formatMessage('EmptyState.NoResults.Text')}
          actionLabel={formatMessage('EmptyState.NoResults.ClearSearch')}
          onAction={() => this.props.onSearchPortfolios('')}
        />
      );
    }

    return <GenericEmptyState header={formatMessage('EmptyState.NoModelPortfolios.Title')} />;
  };

  renderPortfoliosSection = (title: string, loadMore: () => void, portfolios?: Portfolio[], hasMore?: boolean) => {
    const { loading, displayedColumns, displayedMetrics, comparingPortfolioIds, enterprise, user, isThemeLight } = this.props;
    const { initialized, selectedOption } = this.state;

    if (!loading && portfolios?.length === 0) {
      return this.renderNoResults();
    }

    const tableColumns =
      selectedOption === PortfolioOption.ALL_MODELS
        ? [...displayedColumns, PORTFOLIO_TABLE_COLUMNS.LOCK, PORTFOLIO_TABLE_COLUMNS.GROUP]
        : [...displayedColumns, PORTFOLIO_TABLE_COLUMNS.LOCK];

    return (
      <>
        <PortfoliosTable<Portfolio>
          className={classNames({ loading })}
          portfolios={portfolios}
          displayedColumns={tableColumns}
          displayedMetrics={displayedMetrics}
          onDelete={this.props.onDeletePortfolio}
          onEdit={this.props.onEditPortfolio}
          comparingPortfolioIds={comparingPortfolioIds}
          onSort={this.props.onSort}
          onCompare={this.props.onComparePortfolio}
          onPortfoliosSelected={this.props.onPortfoliosSelected}
          onUnselectPortfolios={this.props.onUnselectPortfolios}
          selectedTitle={this.props.selectedTitle}
          locked={!!enterprise}
          forceSelectable
          missingProductPredicate={portfolio => getPortfolioMissingHoldings(portfolio).length > 0}
          user={user}
          isThemeLight={isThemeLight}
          canEditPredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.edit}
          canClonePredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.clone}
        />
        {initialized && !loading && portfolios && portfolios.length > 0 && hasMore && (
          <Waypoint scrollableAncestor={this.getScrollableAncestor()} onEnter={() => loadMore()}>
            <div>
              <LoadingSpinner />
            </div>
          </Waypoint>
        )}
      </>
    );
  };

  getPortfolios = () => {
    const { portfolios = [], enterprise } = this.props;

    let data = portfolios.slice();

    if (enterprise) {
      data = data.concat(enterprise.portfolios || []);
    }

    return data;
  };

  getScrollableAncestor = () => {
    const { scrollAncestor } = this.props;

    if (scrollAncestor) {
      if (typeof scrollAncestor === 'string') {
        return document.querySelector(scrollAncestor);
      }

      return scrollAncestor;
    }

    return this.scrollContainer;
  };

  getOptions = () => {
    const { enterprise, user } = this.props;
    return [
      {
        value: PortfolioOption.ALL_MODELS,
        label: formatMessage('Modal.ModelsPortfoliosList.AllModels'),
        hidden:
          !userHasPermission(user, PERMISSIONS.ALL_MODELS_LIST) ||
          !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST) ||
          !userHasPermission(user, PERMISSIONS.USER_MODELS),
      },
      {
        value: PortfolioOption.MY_MODELS,
        label: formatMessage('Modal.ModelsPortfoliosList.MyModels'),
        hidden: !userHasPermission(user, PERMISSIONS.USER_MODELS),
      },
      {
        value: PortfolioOption.ENTERPRISE_MODELS,
        label: enterprise && formatMessage('Modal.ModelsPortfoliosList.EnterpriseTitle'),
        hidden: !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST),
      },
    ].filter(i => !i.hidden);
  };

  getSelectOptions = () => {
    const { enterprise, user } = this.props;
    return [
      {
        value: PortfolioOption.ALL_MODELS,
        label: formatMessage('Modal.ModelsPortfoliosList.AllModels'),
        hidden: !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST) || !userHasPermission(user, PERMISSIONS.USER_MODELS),
      },
      {
        value: PortfolioOption.MY_MODELS,
        label: formatMessage('Modal.ModelsPortfoliosList.MyModels'),
        hidden: !userHasPermission(user, PERMISSIONS.USER_MODELS),
      },
      {
        value: PortfolioOption.ENTERPRISE_MODELS,
        label: enterprise && formatMessage('Modal.ModelsPortfoliosList.EnterpriseTitle'),
        hidden: !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST),
      },
    ].filter(i => !i.hidden);
  };

  onOptionChange = (option: PortfolioOption) => {
    this.setState({ selectedOption: option });
    this.props.resetSelectedPortfolios();
  };

  render() {
    const {
      portfolios,
      myPortfolios,
      enterprisePortfolios,
      enterprise,
      loading,
      query,
      hasMorePortfolios,
      hasMoreMyPortfolios,
      hasMoreEnterprisePortfolios,
      loadMorePortfolios,
      loadMoreMyPortfolios,
      loadMoreEnterprisePortfolios,
    } = this.props;
    const { selectedOption } = this.state;

    const showLoadingState = loading;

    const portfolioTitles =
      enterprise && formatMessage('Modal.ModelsPortfoliosList.EnterpriseTitle', { name: enterprise.name, count: enterprise.name.endsWith('s') ? 1 : 0 });

    return (
      <>
        <div className={classNames('models-portfolios-list d-flex flex-column')}>
          <SearchInput
            compact
            loading={loading}
            placeholder={formatMessage('Modal.ModelsPortfoliosList.SearchPortfolios')}
            onChange={this.props.onSearchPortfolios}
            value={query}
            data-private
            data-dd-privacy="mask"
          />
          <div className="models-portfolios-list-wrapper">
            <Tabs onChange={option => this.onOptionChange(option)} selected={selectedOption}>
              {this.getOptions().map(option => (
                <ControlItem key={option.value} value={option.value}>
                  <span data-private data-dd-privacy="mask">
                    {option.label}
                  </span>
                </ControlItem>
              ))}
            </Tabs>
          </div>

          <PerfectScrollbar className="portfolios-scrollbar" containerRef={this.setContainerRef}>
            {selectedOption === PortfolioOption.ALL_MODELS &&
              enterprise &&
              this.renderPortfoliosSection(formatMessage('Modal.ModelsPortfoliosList.AllModels'), loadMorePortfolios, portfolios, hasMorePortfolios)}
            {selectedOption === PortfolioOption.MY_MODELS &&
              this.renderPortfoliosSection(formatMessage('Modal.ModelsPortfoliosList.MyModels'), loadMoreMyPortfolios, myPortfolios, hasMoreMyPortfolios)}
            {selectedOption === PortfolioOption.ENTERPRISE_MODELS &&
              portfolioTitles &&
              enterprise &&
              this.renderPortfoliosSection(portfolioTitles, loadMoreEnterprisePortfolios, enterprisePortfolios, hasMoreEnterprisePortfolios)}
            {showLoadingState && <LoadingSpinner className="initial-loading" message={formatMessage('Modal.ModelsPortfoliosList.LoadingPortfolios')} />}
          </PerfectScrollbar>
        </div>
        {!loading && <DisclaimerFooter disclaimers={[formatMessage('Disclaimers.NET_PERFORMANCE')]} />}
      </>
    );
  }
}

export default LegacyModelsPortfoliosList;
